import { registerRendererLayer, type RendererLayerArgs } from "@luxalgo/vela";

/**
 * Coloured price tags on the chart's price axis — the selected trade's entry and exit — like
 * the chart's own last-price tag, each with a dashed rail from its fill to the axis. Drawn
 * here, not as chart drawings, so they never stretch the price scale. Vela has no API for
 * extra axis tags, so this is a renderer layer (it gets the chart's price scale each frame)
 * fed through `setNativeData`.
 */
export const TESSERA_AXIS_TAGS_LAYER = "tessera-axis-tags";

/**
 * A price tag on the axis, with a dashed rail to it from `fromMs` (the fill's time).
 * `quiet`: a secondary fill (a partial close) — dotted rail, outlined tag with `note`
 * ("50%"), moved off any tag it would cover.
 */
export type TesseraAxisTag = {
  price: number;
  color: string;
  fromMs?: number | null;
  quiet?: boolean;
  note?: string | null;
};

const TAG_H = 18;

let registered = false;

/** Register once, before the chart mounts (renderers pick layers up at mount). */
export function ensureTesseraAxisTagsLayer(): void {
  if (registered) return;
  registered = true;
  registerRendererLayer({
    id: TESSERA_AXIS_TAGS_LAYER,
    placement: "above-data",
    create() {
      let canvas: HTMLCanvasElement | null = null;
      return {
        mount(c: HTMLCanvasElement) {
          canvas = c;
        },
        render(args: RendererLayerArgs) {
          if (!canvas) return;
          const ctx = canvas.getContext("2d");
          if (!ctx) return;
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          const tags = Array.isArray(args.data) ? (args.data as TesseraAxisTag[]) : [];
          if (tags.length === 0) return;
          const dpr = args.coords.dpr || 1;
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          const dataW = args.coords.width;
          const { bounds, scale } = args;
          const family = args.theme.fontFamily || "sans-serif";
          ctx.textBaseline = "middle";
          // Main tags first, so a quiet one (a partial) moves off them, never the other way.
          const ordered = [...tags.filter((t) => !t.quiet), ...tags.filter((t) => t.quiet)];
          const taken: number[] = []; // centre y of each tag drawn
          for (const tag of ordered) {
            if (!Number.isFinite(tag.price)) continue;
            const y = args.coords.priceToY(tag.price, scale, bounds);
            if (y < bounds.top + 6 || y > bounds.top + bounds.height - 6) continue;
            if (tag.fromMs != null && Number.isFinite(tag.fromMs)) {
              const x0 = Math.max(0, args.coords.timeToX(tag.fromMs));
              if (x0 < dataW - 40) {
                const yy = Math.round(y) + 0.5;
                ctx.strokeStyle = tag.color;
                ctx.lineWidth = 1;
                ctx.globalAlpha = tag.quiet ? 0.7 : 1;
                ctx.setLineDash(tag.quiet ? [1, 3] : [4, 3]);
                ctx.beginPath();
                ctx.moveTo(x0, yy);
                ctx.lineTo(dataW, yy);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.globalAlpha = 1;
              }
            }
            // The rail stays at the price; only the tag steps aside when it would cover another.
            let ty = Math.round(y);
            if (tag.quiet) {
              const clash = (c: number) => taken.some((t) => Math.abs(t - c) < TAG_H);
              for (const step of [0, TAG_H, -TAG_H, 2 * TAG_H, -2 * TAG_H]) {
                if (!clash(Math.round(y) + step)) {
                  ty = Math.round(y) + step;
                  break;
                }
              }
            }
            taken.push(ty);
            // The axis column is painted over layers, so the tag sits just inside the plot,
            // flush against the axis.
            ctx.font = `${tag.quiet ? 10 : 11}px ${family}`;
            const text = tag.quiet && tag.note ? `${formatTagPrice(tag.price)} · ${tag.note}` : formatTagPrice(tag.price);
            const w = ctx.measureText(text).width + 10;
            const x = dataW - w;
            ctx.beginPath();
            if (typeof ctx.roundRect === "function") ctx.roundRect(x + 0.5, ty - 8.5, w - 1, TAG_H - 1, 3);
            else ctx.rect(x + 0.5, ty - 8.5, w - 1, TAG_H - 1);
            if (tag.quiet) {
              // Outlined: dark fill, coloured edge and text — reads as secondary to the solid exit tag.
              ctx.fillStyle = "rgba(11, 18, 23, 0.9)";
              ctx.fill();
              ctx.strokeStyle = tag.color;
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.fillStyle = tag.color;
            } else {
              ctx.fillStyle = tag.color;
              ctx.fill();
              ctx.fillStyle = readableTextOn(tag.color);
            }
            ctx.textAlign = "left";
            ctx.fillText(text, x + 5, ty + 0.5);
          }
        },
        destroy() {
          canvas = null;
        },
      };
    },
  });
}

/** Same rough precision as the axis: fewer decimals for bigger prices. */
export function formatTagPrice(price: number): string {
  const abs = Math.abs(price);
  const decimals = abs >= 1000 ? 2 : abs >= 10 ? 3 : abs >= 1 ? 4 : 5;
  return price.toFixed(decimals);
}

function readableTextOn(color: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return "#ffffff";
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#0b1217" : "#ffffff";
}
